import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss/types/layout/models';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import { ButtonVariantType } from '@gn-audio/jss-components';

export interface CondensedMenuItem {
  anchor: string;
  anchorElement: RefObject<HTMLElement>;
  name?: string;
}

interface CondensedMenuFields {
  linkText?: Field<string>;
  linkButtonVariant?: Field<ButtonVariantType>;
  link?: LinkField;
  linkAriaLabel?: Field<string>;
  showBuyProductAction?: Field<boolean>;
  showLink?: Field<boolean>;
}

export interface CondensedMenuSettings {
  linkText?: string;
  linkButtonVariant?: ButtonVariantType;
  link?: LinkField;
  showBuyProductAction?: boolean;
  showLink?: boolean;
  linkAriaLabel?: string;
}

export interface ICondensedMenuContext {
  registerItem: (item: CondensedMenuItem) => void;
  unregisterItem: (anchor: string) => void;
  menuItems: CondensedMenuItem[];
  showMenu: boolean;
  settings: CondensedMenuSettings;
}

const defaultContextValue: ICondensedMenuContext = {
  registerItem: () => {},
  unregisterItem: () => {},
  showMenu: false,
  menuItems: [],
  settings: {},
};

export const CondensedMenuContext = createContext<ICondensedMenuContext>(defaultContextValue);
export const useCondensedMenu = () => {
  return useContext(CondensedMenuContext);
};

export interface CondensedMenuContextProps {
  children: ReactNode;
}

export const CondensedMenuProvider = ({ children }: CondensedMenuContextProps) => {
  const [menuItems, setMenuItems] = useState<CondensedMenuItem[]>([]);
  const { sitecoreContext } = useSitecoreContext();

  const placeholders = sitecoreContext.route?.placeholders ?? {};
  const renderings = Object.values(placeholders).flat() as ComponentRendering[];

  const condensedMenuRendering = renderings.find(
    (component) => component.componentName === 'CondensedMenu'
  );
  const showMenu = !!condensedMenuRendering;

  const condensedMenuSettingsFields =
    condensedMenuRendering?.fields && (condensedMenuRendering.fields as CondensedMenuFields);

  const linkText = condensedMenuSettingsFields?.linkText?.value;
  const linkAriaLabel = condensedMenuSettingsFields?.linkAriaLabel?.value;
  const linkButtonVariant: ButtonVariantType =
    condensedMenuSettingsFields?.linkButtonVariant?.value ?? 'quaternary';

  const link = condensedMenuSettingsFields?.link;
  const showBuyProductAction = !!condensedMenuSettingsFields?.showBuyProductAction?.value;
  const showLink = !!condensedMenuSettingsFields?.showLink?.value;

  const registerItem = useCallback((newItem: CondensedMenuItem) => {
    setMenuItems((currentItems) => {
      const hasItem = currentItems.some((itm) => itm.anchor === newItem.anchor);
      if (!hasItem) {
        return [...currentItems, newItem];
      }
      return currentItems.map((item) => {
        if (item.anchor === newItem.anchor) {
          return newItem;
        }
        return item;
      });
    });
  }, []);

  const unregisterItem = useCallback((anchor: string) => {
    setMenuItems((currentItems) => currentItems.filter((itm) => itm.anchor !== anchor));
  }, []);

  const contextValue = useMemo(() => {
    const settings = {
      linkText: linkText,
      linkButtonVariant: linkButtonVariant,
      link: link,
      showLink: showLink,
      showBuyProductAction,
      linkAriaLabel,
    };
    return {
      settings,
      menuItems,
      registerItem,
      unregisterItem,
      showMenu,
    };
  }, [
    unregisterItem,
    registerItem,
    menuItems,
    linkText,
    linkButtonVariant,
    link,
    showLink,
    linkAriaLabel,
    showBuyProductAction,
    showMenu,
  ]);

  return (
    <CondensedMenuContext.Provider value={contextValue}>{children}</CondensedMenuContext.Provider>
  );
};
